import {
  Box,
  Checkbox,
  FormControlLabel,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { useFieldArray, Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useState, useMemo } from 'react'

import {
  FormLayout,
  FormPageHeader,
  FormQuestionDiv,
  NestedLabel1,
  NestedLabel2,
  StickyFormLabel
} from '../styles/forms'
import { causesOfDecline } from '../data/questions'
import { causesOfDeclineOptions } from '../data/causesOfDeclineOptions'
import { ContentWrapper } from '../styles/containers'
import { ErrorText, PageSubtitle, PageTitle } from '../styles/typography'
import { questionMapping } from '../data/questionMapping'
import FormValidationMessageIfErrors from './FormValidationMessageIfErrors'
import language from '../language'
import QuestionNav from './QuestionNav'
import RequiredIndicator from './RequiredIndicator'
import { useInitializeQuestionMappedForm } from '../library/question-mapped-form/useInitializeQuestionMappedForm'
import useSiteInfo from '../library/useSiteInfo'

function CausesOfDeclineForm() {
  const { site_name } = useSiteInfo()
  const form = useFormContext()

  // get functions to build form with useForm() hook
  const { control, formState, watch } = form
  const { errors } = formState
  const {
    fields: causesOfDeclineFields,
    append: causesOfDeclineAppend,
    remove: causesOfDeclineRemove,
    update: causesOfDeclineUpdate
  } = useFieldArray({ name: 'causesOfDecline', control })
  const lossKnownWatcher = watch('lossKnown')

  const [causesOfDeclineTypesChecked, setCausesOfDeclineTypesChecked] = useState([])
  const { siteId } = useParams()
  const apiAnswersUrl = `${process.env.REACT_APP_API_URL}/sites/${siteId}/registration_intervention_answers`

  useInitializeQuestionMappedForm({
    key: 'causesOfDecline',
    apiUrl: apiAnswersUrl,
    form,
    questionMapping
  })

  // big function with many different cases for Q4.2 due to the nesting involved in this question type
  const handleCausesOfDeclineOnChange = ({
    event,
    mainCauseLabel,
    subCauseLabel,
    childOption,
    secondaryChildOption
  }) => {
    const mainCauseIndex = causesOfDeclineFields.findIndex(
      (cause) => cause.mainCauseLabel === mainCauseLabel
    )
    const currentMainCause = causesOfDeclineFields[mainCauseIndex]
    const subCauseIndex = currentMainCause?.subCauses?.findIndex(
      (subCause) => subCause.subCauseLabel === subCauseLabel
    )
    const currentSubCause = currentMainCause?.subCauses?.[subCauseIndex]
    const causesOfDeclineTypesCheckedCopy = [...causesOfDeclineTypesChecked]

    //  case: checked, no subCause, and mainCause does not exist
    if (event.target.checked && !subCauseLabel && mainCauseIndex === -1) {
      causesOfDeclineAppend({
        mainCauseLabel,
        mainCauseAnswers: [{ mainCauseAnswer: childOption, levelOfDegradation: '' }]
      })
      causesOfDeclineTypesCheckedCopy.push(`${mainCauseLabel}-${childOption}`)
    }
    // case: checked, no subCause, mainCause exists
    else if (event.target.checked && !subCauseLabel && mainCauseIndex > -1) {
      currentMainCause.mainCauseAnswers.push({
        mainCauseAnswer: childOption,
        levelOfDegradation: ''
      })
      causesOfDeclineUpdate(currentMainCause)
      causesOfDeclineTypesCheckedCopy.push(`${mainCauseLabel}-${childOption}`)
    }
    // case: checked, subCause, mainCause does not exist
    else if (event.target.checked && subCauseLabel && mainCauseIndex === -1) {
      causesOfDeclineAppend({
        mainCauseLabel,
        subCauses: [
          {
            subCauseLabel,
            subCauseAnswers: [{ subCauseAnswer: secondaryChildOption, levelOfDegradation: '' }]
          }
        ]
      })
      causesOfDeclineTypesCheckedCopy.push(`${subCauseLabel}-${secondaryChildOption}`)
    }
    // case: checked, subCause, mainCause does exist
    else if (event.target.checked && subCauseLabel && mainCauseIndex > -1) {
      // if subCause does not exist within main cause
      if (subCauseIndex === -1) {
        currentMainCause.subCauses.push({
          subCauseLabel,
          subCauseAnswers: [{ subCauseAnswer: secondaryChildOption, levelOfDegradation: '' }]
        })
        causesOfDeclineUpdate(currentMainCause)
      }
      // if subCause does exist within main cause
      else {
        currentSubCause.subCauseAnswers.push({
          subCauseAnswer: secondaryChildOption,
          levelOfDegradation: ''
        })
        causesOfDeclineUpdate(currentMainCause)
      }
      causesOfDeclineTypesCheckedCopy.push(`${subCauseLabel}-${secondaryChildOption}`)
    }
    // case: unchecked, no subCause
    else if (!event.target.checked && !subCauseLabel) {
      // if only one answer exists within mainCauseAnswers
      if (currentMainCause.mainCauseAnswers.length === 1) {
        causesOfDeclineRemove(mainCauseIndex)
      }
      // if more than one answer exists within mainCauseAnwers
      else {
        const childOptionIndex = currentMainCause.mainCauseAnswers.findIndex(
          (option) => option.mainCauseAnswer === childOption
        )
        currentMainCause.mainCauseAnswers.splice(childOptionIndex, 1)
        causesOfDeclineUpdate(currentMainCause)
      }
      const typeIndex = causesOfDeclineTypesCheckedCopy.findIndex(
        (type) => type === `${mainCauseLabel}-${childOption}`
      )
      causesOfDeclineTypesCheckedCopy.splice(typeIndex, 1)
    }
    // case: unchecked, subCause exists
    else if (!event.target.checked && subCauseLabel) {
      // if only one answer exists within subCauseAnswers
      if (currentSubCause.subCauseAnswers.length === 1) {
        currentMainCause.subCauses.splice(subCauseIndex, 1)
        causesOfDeclineUpdate(currentMainCause)
      }
      // if more than one answer exists with subCauseAnswers
      else {
        const secondaryChildOptionIndex = currentSubCause.subCauseAnswers.findIndex(
          (option) => option.subCauseAnswer === secondaryChildOption
        )
        currentSubCause.subCauseAnswers.splice(secondaryChildOptionIndex, 1)
        causesOfDeclineUpdate(currentMainCause)
      }
      // remove main cause if subcauses array is empty
      if (currentMainCause.subCauses.length === 0) {
        causesOfDeclineRemove(mainCauseIndex)
      }
      const typeIndex = causesOfDeclineTypesCheckedCopy.findIndex(
        (type) => type === `${subCauseLabel}-${secondaryChildOption}`
      )
      causesOfDeclineTypesCheckedCopy.splice(typeIndex, 1)
    }
    setCausesOfDeclineTypesChecked(causesOfDeclineTypesCheckedCopy)
  }

  // const onSubmit = async (data) => {
  //   const fields = Object.keys(questionMapping['causesOfDecline'])
  //   const ok = await form.trigger(fields, { shouldFocus: true })

  //   if (!ok) {
  //     setIsError(true)
  //     toast.error(language.error.validation)
  //     return
  //   }
  //   setIsSubmitting(true)
  //   setIsError(false)
  //   if (!data) return

  //   axios
  //     .patch(apiAnswersUrl, mapDataForApi('causesOfDecline', data))
  //     .then(() => {
  //       setIsSubmitting(false)
  //       toast.success(language.success.submit)
  //     })
  //     .catch(() => {
  //       setIsError(true)
  //       setIsSubmitting(false)
  //       toast.error(language.error.apiLoad)
  //     })
  // }

  const causesOfDeclineChecked = useMemo(() => {
    const causes = form.getValues('causesOfDecline') ?? []
    return causes.flatMap((cause) => {
      return (cause.mainCauseAnswers ?? []).map(
        (option) => `${cause.mainCauseLabel}-${option.mainCauseAnswer}`
      )
    })
  }, [form])

  return (
    <ContentWrapper>
      <FormPageHeader>
        <PageTitle>{language.pages.siteQuestionsOverview.formName.causesOfDecline}</PageTitle>
        <PageSubtitle>{site_name}</PageSubtitle>
      </FormPageHeader>
      <QuestionNav
        isFormSaving={false}
        isFormSaveError={false}
        currentSection='causes-of-decline'
      />
      <FormValidationMessageIfErrors formErrors={errors} />
      {/* <FormProvider> */}
      <FormLayout>
        <FormQuestionDiv>
          <StickyFormLabel>{causesOfDecline.lossKnown.question}</StickyFormLabel>
          <Controller
            name='lossKnown'
            control={control}
            defaultValue={false}
            render={({ field }) => {
              return (
                <RadioGroup
                  {...field}
                  aria-labelledby='demo-radio-buttons-group-label'
                  name='radio-buttons-group'>
                  {/* Mui converts values to strings, even for booleans */}
                  <FormControlLabel value={true} control={<Radio />} label='Yes' />
                  <FormControlLabel value={false} control={<Radio />} label='No' />
                </RadioGroup>
              )
            }}
          />
        </FormQuestionDiv>
        {lossKnownWatcher === 'true' ? (
          <FormQuestionDiv>
            <StickyFormLabel>
              {causesOfDecline.causesOfDecline.question} <RequiredIndicator />
            </StickyFormLabel>
            {causesOfDeclineOptions.map((mainCause, mainCauseIndex) => {
              return (
                <Box key={mainCauseIndex} sx={{ marginTop: '0.75em', marginBottom: '1.5em' }}>
                  <NestedLabel1>{mainCause.label}</NestedLabel1>
                  {typeof mainCause.children[0] === 'string'
                    ? mainCause.children.map((childOption, childIndex) => (
                        <Box key={childIndex}>
                          <Box>
                            <ListItem>
                              <Checkbox
                                value={childOption}
                                checked={causesOfDeclineChecked.includes(
                                  `${mainCause.label}-${childOption}`
                                )}
                                onChange={(event) =>
                                  handleCausesOfDeclineOnChange({
                                    event,
                                    mainCauseLabel: mainCause.label,
                                    childOption
                                  })
                                }></Checkbox>
                              <Typography variant='subtitle2'>{childOption}</Typography>
                            </ListItem>
                          </Box>
                        </Box>
                      ))
                    : mainCause.children.map((subCause, subCauseIndex) => (
                        <Box
                          key={subCauseIndex}
                          variant='subtitle2'
                          sx={{ marginLeft: '1em', marginTop: '0.75em' }}>
                          <NestedLabel2>{subCause.secondaryLabel}</NestedLabel2>
                          {subCause.secondaryChildren.map(
                            (secondaryChildOption, secondaryChildIndex) => {
                              return (
                                <ListItem key={secondaryChildIndex}>
                                  <Checkbox
                                    value={secondaryChildOption}
                                    checked={causesOfDeclineTypesChecked.includes(
                                      `${subCause.secondaryLabel}-${secondaryChildOption}`
                                    )}
                                    onChange={(event) =>
                                      handleCausesOfDeclineOnChange({
                                        event,
                                        mainCauseLabel: mainCause.label,
                                        subCauseLabel: subCause.secondaryLabel,
                                        secondaryChildOption
                                      })
                                    }></Checkbox>
                                  <Typography variant='subtitle2'>
                                    {secondaryChildOption}
                                  </Typography>
                                </ListItem>
                              )
                            }
                          )}
                        </Box>
                      ))}
                </Box>
              )
            })}
            <ErrorText>{errors?.causesOfDecline?.message}</ErrorText>
          </FormQuestionDiv>
        ) : null}
        {causesOfDeclineFields.length ? (
          <FormQuestionDiv>
            <StickyFormLabel>{causesOfDecline.levelsOfDegredation.question}</StickyFormLabel>
            {causesOfDeclineFields?.map((mainCause, mainCauseIndex) => (
              <Box key={mainCauseIndex}>
                <NestedLabel1>{mainCause.mainCauseLabel}</NestedLabel1>
                {mainCause.mainCauseAnswers?.map((answer, answerIndex) => {
                  return (
                    <Box key={answerIndex}>
                      <Typography sx={{ marginLeft: '0.75em' }} variant='subtitle2'>
                        {answer.mainCauseAnswer}
                      </Typography>
                      <Controller
                        name={`causesOfDecline.${mainCauseIndex}.mainCauseAnswers.${answerIndex}.levelOfDegradation`}
                        control={control}
                        defaultValue=''
                        required
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            required
                            value={field.value}
                            label='Magnitude of impact *'
                            sx={{
                              width: '13em',
                              marginLeft: '0.5em',
                              marginTop: '0.5em',
                              marginBottom: '1.5em'
                            }}>
                            {causesOfDecline.levelsOfDegredation.options.map((item, index) => (
                              <MenuItem key={index} value={item} sx={{ fontSize: '1.8rem' }}>
                                {item}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Box>
                  )
                })}
                {mainCause.subCauses?.map((subCause, subCauseIndex) => {
                  return (
                    <Box key={subCauseIndex}>
                      <NestedLabel2>{subCause.subCauseLabel}</NestedLabel2>
                      {subCause.subCauseAnswers?.map((subCauseAnswer, subCauseAnswerIndex) => {
                        return (
                          <Box key={subCauseAnswerIndex}>
                            <Typography sx={{ marginLeft: '0.75em' }} variant='subtitle2'>
                              {subCauseAnswer.subCauseAnswer}
                            </Typography>
                            <Controller
                              name={`causesOfDecline.${mainCauseIndex}.subCauses.${subCauseIndex}.subCauseAnswers.${subCauseAnswerIndex}.levelOfDegradation`}
                              control={control}
                              defaultValue=''
                              required
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  select
                                  value={field.value}
                                  label='Magnitude of impact *'
                                  sx={{
                                    width: '13em',
                                    marginLeft: '0.5em',
                                    marginTop: '0.5em',
                                    marginBottom: '1em'
                                  }}>
                                  {causesOfDecline?.levelsOfDegredation.options?.map(
                                    (item, index) => (
                                      <MenuItem key={index} value={item}>
                                        {item}
                                      </MenuItem>
                                    )
                                  )}
                                </TextField>
                              )}
                            />
                          </Box>
                        )
                      })}
                    </Box>
                  )
                })}
              </Box>
            ))}
            <ErrorText>
              {/* if error msg exists, this has to do with MOI. Reduces complexity in flitering */}
              {errors?.causesOfDecline?.length
                ? `Please select magnitude of impact for each item`
                : null}
            </ErrorText>
          </FormQuestionDiv>
        ) : null}
      </FormLayout>
      {/* </FormProvider> */}
    </ContentWrapper>
  )
}

export default CausesOfDeclineForm
